import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Sparkles, Palette, Sprout, Gift } from "lucide-react";
import { setPageMeta } from "@/lib/pageMeta";
import "./DiwaliCataloguePage.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Runtime override — the PDF renderer sets `window.__CAT_API_BASE__` before
// navigating so the browser fetches from localhost:8001 instead of the
// external URL (avoids CDN CORS + credentials on the wildcard origin).
function catalogueApiBase() {
  if (typeof window !== "undefined" && window.__CAT_API_BASE__) {
    return window.__CAT_API_BASE__;
  }
  return API;
}

/* ----------------------------- helpers ----------------------------- */
function assetUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}
const fmt = (n) => (n == null ? "" : `₹${Number(n).toLocaleString("en-IN")}`);
const bucketLabel = (lo, hi) => `₹${lo.toLocaleString("en-IN")}–₹${hi.toLocaleString("en-IN")}`;

/* ----------------------------- shared frame ----------------------------- */
function Slide({ children, variant = "cream", id }) {
  return (
    <section className={`cat-slide cat-slide--${variant}`} id={id} data-testid={id}>
      <div className="cat-slide-inner">{children}</div>
    </section>
  );
}

function Placeholder({ label }) {
  return (
    <div className="cat-ph" aria-label={`Placeholder — ${label}`}>
      <span>{label}</span>
    </div>
  );
}

/* ============================== SLIDES ============================== */

function CoverSlide({ image }) {
  return (
    <Slide variant="ink" id="slide-cover">
      <div className="cat-cover">
        <div className="cat-cover-brand">Utsah</div>
        <div className="cat-cover-title">
          <div className="cat-eyebrow cat-eyebrow--light">Diwali Catalogue 2026</div>
          <h1 className="cat-h1 cat-h1--light">
            Celebrate <em>every moment.</em>
          </h1>
          <p className="cat-sub cat-sub--light">Thoughtful Corporate Gifting.</p>
        </div>
        <div className="cat-cover-media">
          {image ? (
            <img src={assetUrl(image)} alt="Utsah Diwali 2026" />
          ) : (
            <Placeholder label="HERO IMAGE" />
          )}
        </div>
      </div>
    </Slide>
  );
}

function CollectionSlide({ image }) {
  return (
    <Slide variant="cream" id="slide-collection">
      <div className="cat-collection">
        <div className="cat-collection-text">
          <div className="cat-eyebrow">The Utsah Collection</div>
          <h2 className="cat-h1">
            Diwali <em>Collection 2026</em>
          </h2>
          <p className="cat-sub">
            Thoughtfully curated gifting options for a memorable Diwali.
          </p>
        </div>
        <div className="cat-collection-media">
          {image ? (
            <img src={assetUrl(image)} alt="Diwali Collection 2026" />
          ) : (
            <Placeholder label="PRIMARY DIWALI VISUAL" />
          )}
        </div>
      </div>
    </Slide>
  );
}

function BudgetSelectorSlide({ buckets, onJump }) {
  return (
    <Slide variant="cream" id="slide-budget-selector">
      <div className="cat-budget-selector">
        <div className="cat-eyebrow">Explore by Budget</div>
        <h2 className="cat-h1">
          Explore Diwali Gifts <em>That Fit Your Budget.</em>
        </h2>
        <p className="cat-sub">Thoughtfully curated gifting options for every budget.</p>
        <div className="cat-budget-grid">
          {buckets.map((b) => (
            <button
              key={`${b.min}-${b.max}`}
              className="cat-budget-tile"
              onClick={() => onJump(`slide-opener-${b.min}-${b.max}`)}
              data-testid={`budget-tile-${b.min}-${b.max}`}
            >
              <span className="cat-budget-tile-range">{bucketLabel(b.min, b.max)}</span>
              <span className="cat-budget-tile-count">
                {b.products.length} {b.products.length === 1 ? "gift" : "gifts"}
              </span>
            </button>
          ))}
        </div>
        <p className="cat-hint">Click on a price range to explore the gifting options.</p>
      </div>
    </Slide>
  );
}

function BucketOpenerSlide({ min, max, visual, count }) {
  return (
    <Slide variant="cream" id={`slide-opener-${min}-${max}`}>
      <div className="cat-opener">
        <div className="cat-opener-text">
          <div className="cat-eyebrow">Gift Options</div>
          <h2 className="cat-h1">
            From <em>{bucketLabel(min, max)}</em>
          </h2>
          <p className="cat-sub">
            {count} thoughtfully curated {count === 1 ? "hamper" : "hampers"} in this range.
          </p>
        </div>
        <div className="cat-opener-media">
          {visual ? (
            <img src={assetUrl(visual)} alt={`${bucketLabel(min, max)} section visual`} />
          ) : (
            <Placeholder label="SECTION VISUAL" />
          )}
        </div>
      </div>
    </Slide>
  );
}

function ProductSlide({ product, index }) {
  const image = product.image;
  return (
    <Slide variant="cream" id={`slide-product-${product.id}`}>
      <div className="cat-product">
        <div className="cat-product-image">
          {image ? (
            <img src={assetUrl(image)} alt={product.name} loading="lazy" />
          ) : (
            <Placeholder label="PRODUCT IMAGE" />
          )}
        </div>
        <div className="cat-product-info">
          <div className="cat-eyebrow">Utsah · Diwali 2026</div>
          <h2 className="cat-product-name">{product.name}</h2>

          <div className="cat-product-prices">
            {product.mrp ? (
              <div className="cat-product-mrp">
                <span className="cat-price-label">MRP</span>
                <span className="cat-price-mrp">{fmt(product.mrp)}</span>
              </div>
            ) : null}
            <div className="cat-product-bulk">
              <span className="cat-price-label">Bulk Price</span>
              <span className="cat-price-bulk">{fmt(product.price)}<span className="cat-price-suffix">/-</span></span>
            </div>
            <div className="cat-product-taxnote">GST &amp; Shipping Extra</div>
          </div>

          {product.contents?.length ? (
            <div className="cat-product-contents">
              <div className="cat-contents-title">Products Inside</div>
              <ul className="cat-contents-list">
                {product.contents.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </Slide>
  );
}

function DesignedToBeYoursSlide({ sleeves }) {
  return (
    <Slide variant="cream" id="slide-designed-to-be-yours">
      <div className="cat-sleeves">
        <div className="cat-eyebrow">Designed to Be Yours</div>
        <h2 className="cat-h1">
          Personalised for <em>your brand.</em>
        </h2>
        <p className="cat-sub">
          Curated sleeve designs and Diwali wish cards, personalised with your company logo.
        </p>
        <div className="cat-sleeves-grid">
          {sleeves.map((s, i) => (
            <div className="cat-sleeve-card" key={i}>
              {s ? (
                <img src={assetUrl(s)} alt={`Sleeve design ${i + 1}`} />
              ) : (
                <Placeholder label={`SLEEVE DESIGN ${i + 1}`} />
              )}
            </div>
          ))}
        </div>
        <p className="cat-tagline">Three designs. One thoughtful finishing touch.</p>
      </div>
    </Slide>
  );
}

function PlantATreeSlide({ image }) {
  return (
    <Slide variant="cream" id="slide-plant-a-tree">
      <div className="cat-tree">
        <div className="cat-tree-media">
          {image ? (
            <img src={assetUrl(image)} alt="Plant a Tree Initiative" />
          ) : (
            <Placeholder label="TREE / NATURE VISUAL" />
          )}
        </div>
        <div className="cat-tree-text">
          <div className="cat-eyebrow">Plant a Tree Initiative</div>
          <h2 className="cat-h1">
            Become part of our <em>Plant a Tree</em> initiative.
          </h2>
          <p className="cat-tagline cat-tagline--strong">One Gift. One Tree.</p>
          <p className="cat-body">
            For every Utsah gift your company orders, we plant a tree through our
            reforestation partners across India. We work with a social
            reforestation enterprise like Grow-Trees.com to support various
            reforestation projects across India.
          </p>
          <p className="cat-body">
            Each recipient receives a Tree Plantation Certificate, connecting
            their gift to the tree planted on your company's behalf.
          </p>
        </div>
      </div>
    </Slide>
  );
}

function TreesTakeRootSlide({ projects }) {
  return (
    <Slide variant="cream" id="slide-trees-take-root">
      <div className="cat-trees-root">
        <div className="cat-trees-head">
          <div className="cat-eyebrow">Reforestation Projects</div>
          <h2 className="cat-h1">
            Where Your Trees <em>Take Root.</em>
          </h2>
          <p className="cat-sub">
            Your Utsah gifts support reforestation projects across India.
          </p>
        </div>
        <div className="cat-trees-grid">
          {projects.slice(0, 4).map((p, i) => (
            <div className="cat-project-card" key={i}>
              <div className="cat-project-image">
                {p.image ? (
                  <img src={assetUrl(p.image)} alt={p.name} />
                ) : (
                  <Placeholder label="PROJECT IMAGE" />
                )}
              </div>
              <div className="cat-project-body">
                <div className="cat-project-name">{p.name}</div>
                {p.location ? <div className="cat-project-location">{p.location}</div> : null}
                {p.description ? <p className="cat-project-desc">{p.description}</p> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function MissionAheadSlide() {
  return (
    <Slide variant="ink" id="slide-mission-ahead">
      <div className="cat-mission">
        <div className="cat-eyebrow cat-eyebrow--light">Our Mission Ahead</div>
        <h2 className="cat-h1 cat-h1--light">
          Every celebration, <em>a lasting impact.</em>
        </h2>

        <div className="cat-mission-grid">
          <div className="cat-mission-card">
            <div className="cat-mission-tag">2026 Target</div>
            <div className="cat-mission-figure">30,000</div>
            <div className="cat-mission-figure-caption">Trees Planted</div>
            <p className="cat-mission-body">
              Our goal for 2026 is to plant 30,000 trees through our Plant a Tree
              Initiative, turning every meaningful gift into an opportunity to give back.
            </p>
          </div>
          <div className="cat-mission-card">
            <div className="cat-mission-tag">2030 Goal</div>
            <div className="cat-mission-figure">3,00,000</div>
            <div className="cat-mission-figure-caption">Trees Across India</div>
            <p className="cat-mission-body">
              By 2030, we aim to plant 3 lakh trees across India through our growing
              network of reforestation projects and gifting partners.
            </p>
          </div>
        </div>

        <div className="cat-mission-vision">
          <div className="cat-eyebrow cat-eyebrow--light">Our Vision</div>
          <p className="cat-vision-line">To make every celebration create a lasting impact.</p>
        </div>
      </div>
    </Slide>
  );
}

function GiftThatGivesBackSlide({ certImage }) {
  return (
    <Slide variant="cream" id="slide-gift-gives-back">
      <div className="cat-gives-back">
        <div className="cat-gives-back-text">
          <div className="cat-eyebrow">A Gift That Gives Back</div>
          <h2 className="cat-h1">
            Every hamper carries <em>a promise.</em>
          </h2>
          <p className="cat-body">
            A Tree Plantation Certificate is included inside every gift hamper,
            connecting your gift to the tree planted through our Plant a Tree
            Initiative.
          </p>
        </div>
        <div className="cat-gives-back-media">
          {certImage ? (
            <img src={assetUrl(certImage)} alt="Tree Plantation Certificate" />
          ) : (
            <Placeholder label="CERTIFICATE IMAGE" />
          )}
        </div>
      </div>
    </Slide>
  );
}

function PartnerBenefitSlide({ brands }) {
  return (
    <Slide variant="cream" id="slide-partner-benefits">
      <div className="cat-partner">
        <div className="cat-eyebrow">Utsah Partner Benefit Program</div>
        <h2 className="cat-h1">
          Exclusive offers from <em>leading brands.</em>
        </h2>
        <p className="cat-sub">
          When your company chooses Utsah, you automatically become a part of our
          Partner Benefit Program. Gift recipients get access to exclusive offers
          from leading brands across India.
        </p>
        <div className="cat-brand-grid">
          {brands.slice(0, 12).map((b, i) => (
            <div className="cat-brand-tile" key={i}>
              {b.logo ? (
                <img src={assetUrl(b.logo)} alt={b.name} />
              ) : (
                <span>{b.name}</span>
              )}
            </div>
          ))}
        </div>
        <p className="cat-tagline">One thoughtful gift. Exclusive offers. More to celebrate.</p>
      </div>
    </Slide>
  );
}

function AboutUtsahSlide({ image }) {
  return (
    <Slide variant="cream" id="slide-about-utsah">
      <div className="cat-about">
        <div className="cat-about-text">
          <div className="cat-eyebrow">About Utsah</div>
          <h2 className="cat-h1">
            Celebrate <em>every moment.</em>
          </h2>
          <p className="cat-body">
            At Utsah, we believe every celebration deserves to be remembered. We
            create thoughtful gifting experiences that help organisations
            celebrate, appreciate, and strengthen the relationships that matter
            most.
          </p>
          <p className="cat-body">
            From welcoming a new employee and recognising milestones to thanking
            clients and celebrating festivals, every gift is thoughtfully curated
            to reflect the emotion behind the occasion. Our aim is simple — to
            turn moments of appreciation into lasting memories.
          </p>
        </div>
        <div className="cat-about-media">
          {image ? (
            <img src={assetUrl(image)} alt="About Utsah" />
          ) : (
            <Placeholder label="ABOUT UTSAH VISUAL" />
          )}
        </div>
      </div>
    </Slide>
  );
}

function RelianceStorySlide({ img1, img2 }) {
  return (
    <Slide variant="cream" id="slide-reliance">
      <div className="cat-reliance">
        <div className="cat-reliance-text">
          <div className="cat-eyebrow">Our Story So Far</div>
          <h2 className="cat-h1">
            From a simple idea to <em>300+ Reliance Smart Bazaar</em> stores.
          </h2>
          <p className="cat-body">
            True Gesture launched during Diwali 2025 across 300+ Reliance Smart
            Bazaar stores across Central India — and became the{" "}
            <strong>second highest-selling dry fruit gift brand</strong> in the region.
          </p>
          <p className="cat-body">
            For us, it wasn't just a retail launch — it was proof that thoughtful
            gifting could resonate at scale.
          </p>
        </div>
        <div className="cat-reliance-media">
          <div className="cat-reliance-tile">
            {img1 ? (
              <img src={assetUrl(img1)} alt="True Gesture in-store" />
            ) : (
              <Placeholder label="STORE / SHELF" />
            )}
          </div>
          <div className="cat-reliance-tile">
            {img2 ? (
              <img src={assetUrl(img2)} alt="True Gesture Reliance store" />
            ) : (
              <Placeholder label="RELIANCE STORE" />
            )}
          </div>
        </div>
      </div>
    </Slide>
  );
}

function WhyUtsahSlide() {
  const points = [
    { Icon: Sparkles, title: "Thoughtful Gifts, Curated for Your Team", body: "Premium gifts selected to make every gifting experience feel personal and meaningful.", label: "Thoughtfully Curated & Gift-Ready" },
    { Icon: Palette, title: "Customised for Your Brand, Free of Charge", body: "Add your company branding to every gift, with complimentary customisation.", label: "Complimentary Branding Included" },
    { Icon: Sprout, title: "Your Gift. Your Tree. Your Impact.", body: "Every eligible gift helps plant a tree on your company's behalf, creating a lasting environmental impact.", label: "One Tree Planted with Every Order" },
    { Icon: Gift, title: "Exclusive Offers from Brands You Love", body: "Every hamper unlocks access to exclusive offers and discounts from our partner brands.", label: "Exclusive Partner Benefit Program" },
  ];
  return (
    <Slide variant="cream" id="slide-why-utsah">
      <div className="cat-why">
        <div className="cat-eyebrow">Why Utsah</div>
        <h2 className="cat-h1">
          More than what's <em>inside the hamper.</em>
        </h2>
        <p className="cat-sub">
          A thoughtful gift creates a memorable first impression. Our Exclusive
          Partner Benefit Program ensures the experience continues long after the
          hamper has been opened.
        </p>
        <div className="cat-why-grid">
          {points.map(({ Icon, title, body, label }, i) => (
            <div className="cat-why-card" key={i}>
              <div className="cat-why-icon"><Icon size={26} strokeWidth={1.6} /></div>
              <div className="cat-why-title">{title}</div>
              <p className="cat-why-body">{body}</p>
              <div className="cat-why-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function ThankYouSlide({ image }) {
  return (
    <Slide variant="ink" id="slide-thank-you">
      <div className="cat-thankyou">
        <div className="cat-thankyou-text">
          <div className="cat-eyebrow cat-eyebrow--light">Utsah</div>
          <h2 className="cat-h1 cat-h1--light">
            Thank <em>you.</em>
          </h2>
          <p className="cat-sub cat-sub--light">
            Thoughtful gifting, designed in India.
          </p>
        </div>
        {image ? (
          <div className="cat-thankyou-media">
            <img src={assetUrl(image)} alt="Utsah" />
          </div>
        ) : null}
      </div>
    </Slide>
  );
}

/* ============================== PAGE ============================== */

export default function DiwaliCataloguePage() {
  const [params] = useSearchParams();
  const isPdfMode = params.get("pdf") === "1";
  const isPreview = params.get("preview") === "1";

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    setPageMeta({
      title: "Utsah Diwali 2026 Catalogue",
      description:
        "Explore Utsah's Diwali 2026 corporate gifting catalogue — thoughtfully curated hampers for every budget, with our Plant a Tree Initiative and Partner Benefit Program.",
    });
  }, []);

  useEffect(() => {
    const url = `${catalogueApiBase()}/diwali-catalogue${isPreview ? "?preview=1" : ""}`;
    axios
      .get(url, { withCredentials: !isPdfMode })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not-published" : "error"));
  }, [isPreview, isPdfMode]);

  // Compose ordered slide list once we have data
  const slides = useMemo(() => {
    if (!data) return [];
    const imgs = data.images || {};
    const list = [
      { key: "cover", el: <CoverSlide image={imgs.catalogue_diwali_2026_cover_image} /> },
      { key: "collection", el: <CollectionSlide image={imgs.catalogue_diwali_2026_collection_visual} /> },
      { key: "budget", el: <BudgetSelectorSlide buckets={data.buckets} onJump={jumpTo} /> },
    ];
    data.buckets.forEach((b) => {
      const visKey = `catalogue_diwali_2026_bucket_${b.min}_${b.max}_visual`;
      list.push({ key: `opener-${b.min}-${b.max}`, el: <BucketOpenerSlide min={b.min} max={b.max} visual={imgs[visKey]} count={b.products.length} /> });
      b.products.forEach((p, i) => {
        list.push({ key: `product-${p.id}`, el: <ProductSlide product={p} index={i} /> });
      });
    });
    list.push({ key: "sleeves", el: <DesignedToBeYoursSlide sleeves={[imgs.catalogue_diwali_2026_sleeve_1, imgs.catalogue_diwali_2026_sleeve_2, imgs.catalogue_diwali_2026_sleeve_3]} /> });
    list.push({ key: "plant-a-tree", el: <PlantATreeSlide image={imgs.catalogue_diwali_2026_tree_visual} /> });
    list.push({ key: "trees-take-root", el: <TreesTakeRootSlide projects={data.green_gift_projects || []} /> });
    list.push({ key: "mission", el: <MissionAheadSlide /> });
    list.push({ key: "gives-back", el: <GiftThatGivesBackSlide certImage={imgs.green_gift_certificate_image} /> });
    list.push({ key: "partner", el: <PartnerBenefitSlide brands={data.partner_brands || []} /> });
    list.push({ key: "about", el: <AboutUtsahSlide image={imgs.catalogue_diwali_2026_about_utsah_image} /> });
    list.push({ key: "reliance", el: <RelianceStorySlide img1={imgs.catalogue_diwali_2026_reliance_image_1} img2={imgs.catalogue_diwali_2026_reliance_image_2} /> });
    list.push({ key: "why-utsah", el: <WhyUtsahSlide /> });
    list.push({ key: "thankyou", el: <ThankYouSlide image={imgs.catalogue_diwali_2026_closing_image} /> });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function jumpTo(id) {
    const idx = slides.findIndex((s) => `slide-${s.key}` === id || s.el?.props?.id === id);
    if (idx >= 0) setSlideIdx(idx);
    else {
      // Fallback: DOM query
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  const go = useCallback(
    (delta) => setSlideIdx((i) => Math.max(0, Math.min(slides.length - 1, i + delta))),
    [slides.length]
  );

  // Keyboard nav (disabled in PDF mode)
  useEffect(() => {
    if (isPdfMode) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === " ") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") setSlideIdx(0);
      else if (e.key === "End") setSlideIdx(slides.length - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, slides.length, isPdfMode]);

  if (error === "not-published") {
    return (
      <div className="cat-blank" data-testid="catalogue-not-published">
        <div className="cat-blank-inner">
          <div className="cat-eyebrow">Utsah</div>
          <h1 className="cat-h1">Coming soon.</h1>
          <p className="cat-sub">The Diwali 2026 catalogue will be available shortly.</p>
        </div>
      </div>
    );
  }
  if (error === "error") {
    return (
      <div className="cat-blank" data-testid="catalogue-error">
        <div className="cat-blank-inner">
          <div className="cat-eyebrow">Utsah</div>
          <h1 className="cat-h1">We couldn't load the catalogue.</h1>
          <p className="cat-sub">Please try again in a moment.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="cat-blank" data-testid="catalogue-loading"><div className="cat-blank-inner"><p className="cat-sub">Loading…</p></div></div>;

  // PDF mode: render every slide top-to-bottom, no controls
  if (isPdfMode) {
    return (
      <div className="cat-pdf-root" data-testid="catalogue-pdf">
        {slides.map((s) => (
          <div className="cat-pdf-page" key={s.key}>
            {s.el}
          </div>
        ))}
      </div>
    );
  }

  // Web mode: single-slide viewer
  const current = slides[slideIdx];
  return (
    <div className="cat-web-root" data-testid="catalogue-web">
      <div className="cat-viewport">{current?.el}</div>

      <div className="cat-controls" data-testid="catalogue-controls">
        <button
          className="cat-nav-btn"
          onClick={() => go(-1)}
          disabled={slideIdx === 0}
          aria-label="Previous slide"
          data-testid="catalogue-prev"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="cat-counter" data-testid="catalogue-counter">
          {slideIdx + 1} / {slides.length}
        </div>
        <button
          className="cat-nav-btn"
          onClick={() => go(1)}
          disabled={slideIdx === slides.length - 1}
          aria-label="Next slide"
          data-testid="catalogue-next"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
